import { privacy } from "@redotech/schema-privacy";
import { safeAssertNever, Tuple } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  AdvancedFilterType,
  createAdvancedFilterDataSchema,
  GenericFilterBuilder,
} from "./generic-advanced-filter-data";

export enum NumberFilterOperator {
  EQUAL = "=",
  LESS_THAN = "<",
  GREATER_THAN = ">",
  BETWEEN = "between",
  /** Opt-in: shown only by filters that list it in `operators`. */
  GREATER_THAN_OR_EQUAL = ">=",
  /** Opt-in: shown only by filters that list it in `operators`. */
  LESS_THAN_OR_EQUAL = "<=",
  /** Opt-in: shown only by filters that list it in `operators`. */
  NOT_EQUAL = "!=",
}
/** What a number filter offers when it lists no `operators`; the newer operators are opt-in. */
export const DEFAULT_NUMBER_FILTER_OPERATORS: readonly NumberFilterOperator[] =
  [
    NumberFilterOperator.EQUAL,
    NumberFilterOperator.LESS_THAN,
    NumberFilterOperator.GREATER_THAN,
    NumberFilterOperator.BETWEEN,
  ];

const NumberFilterOperatorSchema = createEnum(NumberFilterOperator, {
  enumName: "NumberFilterOperator",
}).meta({ privacy: privacy.enumOrStatus });

const TwoNumberTupleSchema = z
  .tuple([
    z.number().meta({ privacy: privacy.quantity }),
    z.number().meta({ privacy: privacy.quantity }),
  ])
  .meta({ privacy: privacy.structured });
const OneNumberTupleSchema = z
  .tuple([z.number().meta({ privacy: privacy.quantity })])
  .meta({ privacy: privacy.structured });
export type TwoNumberTuple = Tuple<number, 2>;
export type OneNumberTuple = Tuple<number, 1>;

const NumberValueSchema = z.union([OneNumberTupleSchema, TwoNumberTupleSchema]);
export type NumberValue = z.infer<typeof NumberValueSchema>;

export const NumberFilterDataSchema = createAdvancedFilterDataSchema(
  AdvancedFilterType.NUMBER,
  NumberValueSchema,
  NumberFilterOperatorSchema,
).extend({
  modifier: z.number().nullish().meta({ privacy: privacy.quantity }),
});
export type NumberFilterData = z.infer<typeof NumberFilterDataSchema>;

export const NumberFilterBuilder: GenericFilterBuilder<
  NumberValue,
  NumberFilterOperator,
  NumberFilterData
> = {
  type: AdvancedFilterType.NUMBER,
  valueSchema: NumberValueSchema,
  operatorSchema: NumberFilterOperatorSchema,
  schema: NumberFilterDataSchema,
  addOpensearchFilters: ({ filter, opensearchPath, compound }) => {
    const { operator, value } = filter;
    if (!value) {
      return;
    }

    // The UI already converts values to storage units. Applying a saved modifier
    // here would convert them twice (e.g. pounds/ounces to grams).
    const numbers = [...value].sort((a, b) => a - b);

    switch (operator) {
      case NumberFilterOperator.BETWEEN: {
        if (numbers.length === 2) {
          compound.filter.push({
            range: { [opensearchPath]: { gte: numbers[0], lte: numbers[1] } },
          });
        }

        break;
      }
      case NumberFilterOperator.LESS_THAN: {
        compound.filter.push({
          range: { [opensearchPath]: { lt: numbers[0] } },
        });

        break;
      }
      case NumberFilterOperator.GREATER_THAN: {
        compound.filter.push({
          range: { [opensearchPath]: { gt: numbers[0] } },
        });

        break;
      }
      case NumberFilterOperator.GREATER_THAN_OR_EQUAL: {
        compound.filter.push({
          range: { [opensearchPath]: { gte: numbers[0] } },
        });

        break;
      }
      case NumberFilterOperator.LESS_THAN_OR_EQUAL: {
        compound.filter.push({
          range: { [opensearchPath]: { lte: numbers[0] } },
        });

        break;
      }
      case NumberFilterOperator.EQUAL: {
        compound.filter.push({ term: { [opensearchPath]: numbers[0] } });

        break;
      }
      case NumberFilterOperator.NOT_EQUAL: {
        compound.must_not.push({ term: { [opensearchPath]: numbers[0] } });

        break;
      }
      default:
        safeAssertNever(operator);
    }
  },
  readFromString(name, operatorValueEncoded, defaultFilter) {
    try {
      const decoded = decodeURIComponent(operatorValueEncoded);
      const operatorValue = decoded.split("::");

      const operator = operatorValue?.[0];
      const value = operatorValue?.[1];

      if (!operator) {
        return defaultFilter;
      }

      const parsedOperator = NumberFilterOperatorSchema.safeParse(operator);
      if (!parsedOperator.success) {
        console.warn("Invalid operator type");
        return undefined;
      }

      if (!value) {
        return {
          type: AdvancedFilterType.NUMBER,
          name,
          value: null,
          operator: parsedOperator.data,
        };
      }

      const parsedValue = NumberValueSchema.safeParse(
        value.trim().split(",").map(Number),
      );

      if (!parsedValue.success) {
        console.warn("Invalid value type for NumberFilter");
        return undefined;
      }

      return {
        type: AdvancedFilterType.NUMBER,
        name,
        value: parsedValue.data,
        operator: parsedOperator.data,
      };
    } catch (error) {
      console.error("Error parsing NumberFilter", error);
      return undefined;
    }
  },
  writeToString(filter) {
    try {
      if (!filter) {
        return "";
      }
      const { operator, value } = filter;
      const parsedValue = NumberValueSchema.safeParse(value);

      if (parsedValue.success) {
        return `${operator}::${parsedValue.data.join(",")}`;
      } else {
        return `${operator}::`;
      }
    } catch (error) {
      console.error("Error writing NumberFilter", error);
      return "";
    }
  },
  isPartial(filter) {
    return (
      !filter.operator ||
      !filter.value ||
      (filter.operator === NumberFilterOperator.BETWEEN &&
        filter.value.length !== 2)
    );
  },
};
